import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import MicIcon from '@mui/icons-material/Mic';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import BarChartIcon from '@mui/icons-material/BarChart';
import { StandardButton } from './design-system';

interface Props {
  open: boolean;
  onClose: () => void;
}

const ACCENT = {
  cyan: '#00E0FF',
  green: '#00E676',
  purple: '#CE93D8',
  amber: '#FFD740',
};

// Visual mockup of the actual SwiftUI widget — rendered in HTML/CSS so
// users see exactly what they'll be adding before they tap Got It.
const WidgetMockup: React.FC = () => (
  <Box
    sx={{
      width: 280,
      height: 132,
      mx: 'auto',
      background: 'linear-gradient(135deg, #0a0e14 0%, #101428 100%)',
      borderRadius: 2,
      p: 1.25,
      display: 'flex',
      gap: 1.25,
      border: '1px solid rgba(0,224,255,0.25)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.5), 0 0 32px rgba(0,224,255,0.15)',
    }}
  >
    {/* Score block */}
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', minWidth: 70 }}>
      <Box className="icon-text-row" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: ACCENT.cyan, boxShadow: `0 0 4px ${ACCENT.cyan}`, flexShrink: 0 }} />
        <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: 8, fontWeight: 700, color: ACCENT.cyan, letterSpacing: 1, lineHeight: 1 }}>
          FUELTRON
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 36, fontWeight: 700, color: ACCENT.green, lineHeight: 1, textShadow: `0 0 8px ${ACCENT.green}50` }}>
        84
      </Typography>
      <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'rgba(255,255,255,0.6)', mt: 0.25 }}>
        Score
      </Typography>
    </Box>

    {/* 2x2 action tiles */}
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 0.75, flex: 1 }}>
      {[
        { icon: <CameraAltIcon sx={{ fontSize: 18 }} />, label: 'SCAN', color: ACCENT.cyan },
        { icon: <MicIcon sx={{ fontSize: 18 }} />, label: 'VOICE', color: ACCENT.green },
        { icon: <FitnessCenterIcon sx={{ fontSize: 18 }} />, label: 'WORKOUT', color: ACCENT.purple },
        { icon: <BarChartIcon sx={{ fontSize: 18 }} />, label: 'SCORE', color: ACCENT.amber },
      ].map((tile) => (
        <Box
          key={tile.label}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${tile.color}40`,
            borderRadius: 1,
            color: tile.color,
            filter: `drop-shadow(0 0 4px ${tile.color}80)`,
          }}
        >
          {tile.icon}
          <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: 7, fontWeight: 700, color: 'rgba(255,255,255,0.85)', letterSpacing: 0.8 }}>
            {tile.label}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const Step: React.FC<{ n: number; children: React.ReactNode }> = ({ n, children }) => (
  <Box className="icon-text-row" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        background: `${ACCENT.cyan}1A`,
        border: `1px solid ${ACCENT.cyan}80`,
        color: ACCENT.cyan,
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        lineHeight: 1,
      }}
    >
      {n}
    </Box>
    <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', lineHeight: 1.5, flex: 1 }}>
      {children}
    </Typography>
  </Box>
);

const SiriPhrase: React.FC<{ phrase: string }> = ({ phrase }) => (
  <Box
    sx={{
      px: 1.25,
      py: 0.75,
      background: 'rgba(206,147,216,0.08)',
      border: '1px solid rgba(206,147,216,0.3)',
      borderRadius: 1,
      mb: 0.75,
    }}
  >
    <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: ACCENT.purple, fontWeight: 600 }}>
      "{phrase}"
    </Typography>
  </Box>
);

const WidgetDiscoveryDialog: React.FC<Props> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'var(--bg-card)',
          backgroundImage: 'none',
          borderRadius: { xs: '16px 16px 0 0', sm: 2 },
          border: '1px solid rgba(0,224,255,0.3)',
          maxHeight: { xs: '92vh', sm: '88vh' },
          margin: { xs: 0, sm: 2 },
          width: { xs: '100%', sm: 'auto' },
          position: { xs: 'fixed', sm: 'relative' },
          bottom: { xs: 0, sm: 'auto' },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          pt: 2,
          pb: 1,
        }}
      >
        <Chip
          label="NEW"
          size="small"
          sx={{
            height: 20,
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 1.5,
            background: `${ACCENT.cyan}1F`,
            color: ACCENT.cyan,
            border: `1px solid ${ACCENT.cyan}80`,
          }}
        />
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: ACCENT.cyan,
            bgcolor: 'rgba(0,224,255,0.1)',
            border: '1px solid rgba(0,224,255,0.2)',
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 2.5, pt: 0.5, pb: 2.5 }}>
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: 22,
            fontWeight: 700,
            background: `linear-gradient(135deg, ${ACCENT.cyan} 0%, ${ACCENT.green} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            mb: 0.5,
            letterSpacing: 0.5,
          }}
        >
          Log meals from your Lock Screen
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', mb: 2.5, lineHeight: 1.5 }}>
          The FuelTron widget sits on your Home Screen or Lock Screen.
          One tap → straight into the camera, voice log, or workout entry.
          No unlock, no app open, no menu hunting.
        </Typography>

        <Box sx={{ my: 2.5 }}>
          <WidgetMockup />
        </Box>

        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            fontWeight: 700,
            color: ACCENT.cyan,
            letterSpacing: 1.5,
            mb: 1.5,
            mt: 1,
          }}
        >
          HOW TO ADD IT
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2.5 }}>
          <Step n={1}>
            Long-press an empty area of your Home Screen until the icons jiggle.
          </Step>
          <Step n={2}>
            Tap the <strong style={{ color: ACCENT.cyan }}>+</strong> button in the top-left corner.
          </Step>
          <Step n={3}>
            Search <strong style={{ color: ACCENT.cyan }}>"FuelTron"</strong>, pick a size, tap{' '}
            <strong style={{ color: ACCENT.cyan }}>Add Widget</strong>.
          </Step>
        </Box>

        <Box
          sx={{
            mt: 2.5,
            pt: 2,
            borderTop: '1px solid rgba(206,147,216,0.2)',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              fontWeight: 700,
              color: ACCENT.purple,
              letterSpacing: 1.5,
              mb: 1.5,
            }}
          >
            HEY SIRI, ASK FUELTRON
          </Typography>
          <SiriPhrase phrase="Hey Siri, what's my FuelTron Score" />
          <SiriPhrase phrase="Hey Siri, scan a meal with FuelTron" />
          <SiriPhrase phrase="Hey Siri, log a workout with FuelTron" />
          <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', mt: 1, fontStyle: 'italic' }}>
            Score readout works without opening the app.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <StandardButton variant="primary" onClick={onClose} sx={{ minWidth: 120 }}>
            Got It
          </StandardButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WidgetDiscoveryDialog;
